import { createContext, useContext, useEffect, useState } from "react"
import { User as FirebaseUser, onAuthStateChanged } from "firebase/auth"
import { User } from "@/entities/user/user"
import { auth } from "@/firebase/firebase"
import { useDocument } from "@/hooks/useFirestore"
import { QueryResult } from "@/utils/mergeQueryStates"
import { getCacheCurrentUser } from "@/entities/user/cache_currentUser"
import { startObserveCollection } from "@/firestore/myFreeReads"
import { startObserveDocument } from "@/firestore/myFreeReadDoc"
import { logger } from "@/utils/logger"

interface AuthContextType extends QueryResult {
   authUser: FirebaseUser | null
   user?: User | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
   const [authUser, setAuthUser] = useState<FirebaseUser | null>(null)
   const [loading, setLoading] = useState(true)
   const { data: user, isError, error } = useDocument<User>("users", authUser?.uid ?? "")

   useEffect(() => {
      const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
         setAuthUser(firebaseUser)
      })

      setTimeout(() => {
         setLoading(false)
      }, 3000)

      return () => unsubscribe()
   }, [])

   useEffect(() => {
      if (!authUser) return

      getCacheCurrentUser().then((cachedUser) => {
         logger('AuthContext.tsx:40 ~ cachedUser', cachedUser);

         if (!cachedUser) {
            throw new Error("No cached user")
         }

         const uid = cachedUser.id
         const isAdmin = cachedUser?.role === "admin"
         const isYoung = cachedUser?.role === "young"

         // Listen to all remote changes
         if (isAdmin) startObserveCollection("users")
         else if (isYoung) startObserveDocument("users", uid)

         startObserveCollection("attendance_events", isYoung ? uid : undefined)
         startObserveCollection("absence_requests", isYoung ? uid : undefined)
         startObserveCollection("school_years")
         startObserveCollection("global_settings")
      })
   }, [authUser])

   useEffect(() => {
      if (!user) return

      setLoading(false)
   }, [user])

   useEffect(() => {
      logger("User changed", { authUser, user })
   }, [authUser, user])

   return (
      <AuthContext.Provider
         value={{
            authUser,
            user,
            isLoading: loading,
            isError,
            error,
            isStale: false,
            isFetching: false,
         }}
      >
         {children}
      </AuthContext.Provider>
   )
}

export function useAuth(): AuthContextType {
   const context = useContext(AuthContext)
   if (context === undefined) {
      throw new Error("useAuth must be used within an AuthProvider")
   }
   return context
}
